import { FC, useContext, useEffect, useRef, useState } from 'react';
import { Box } from '@mui/system';
import MediumTypography from '../../components/formlib/MediumTypography';
import { ReactComponent as DownArrow } from '../../assets/images/downarrowcolored.svg';
import { ReactComponent as UpArrow } from '../../assets/images/uparrowcolored.svg';
import { ReactComponent as InfoIcon } from '../../assets/images/infoIcon.svg';
import InformationModal from './InformationModal';
import ChildandFamilyIFSPOutcomesData from './IFSP/ChildandFamilyIFSPOutcomes.json';
import { ReactComponent as Delete } from '../../assets/images/deleteAlertIcon.svg';
import { ReactComponent as HideIcon } from '../../assets/images/hideIcon.svg';
import { ReactComponent as PrinterIcon } from '../../assets/images/printerIcon.svg';
import { ReactComponent as FilledAddIcon } from '../../assets/images/filledAddIcon.svg';
import { ReactComponent as AddIcon } from '../../assets/images/CircleAdd.svg';
import DatePickerComponent from '../../components/formlib/DatePickerComponent';
import DropdownComponent from '../../components/formlib/DropdownComponent';
import dayjs, { Dayjs } from 'dayjs';
import {
  Grid,
  Paper,
  Table,
  TableBody,
  TableContainer,
  Tooltip,
} from '@mui/material';
import { HeadCell, TableHeader } from '../../components/formlib/TableHeader';
import ButtonComponent from '../../components/formlib/ButtonComponent';
import { Formik, FormikProps } from 'formik';
import * as yup from 'yup';
import IfspContext from './ifspContextApi/IfspContex';
import { datePickerMinDateMaxDateValidate } from '../../utils/dateUtil';
import _ from 'lodash';
import OutcomesProgressInfoTabelRow from './OutcomesProgressInfoTabelRow';
import CommentTextbox from '../../components/formlib/CommentTextbox';

interface ChildandFamilyIFSPOutcomesProps {
  info: boolean;
  open: boolean;
  isIfspScreenLocked: boolean;
  canEditAfterLock: boolean;
  canDeleteAfterLock: boolean;
  onClose: (open: boolean) => void;
  closeInfo: (info: boolean) => void;
  onOpen: (open: boolean) => void;
  openInfo: (info: boolean) => void;
  reviewInfo: boolean;
  openReviewInfo: (reviewInfo: boolean) => void;
  closeReviewInfo: (reviewInfo: boolean) => void;
  isErrorOccured: (isError: boolean) => void;
  isErrorOccuredInOutComeStartDate: (isError: boolean) => void;
  isErrorOccuredInOutComeEndDate: (isError: boolean) => void;
  hasEditPermission: boolean;
}

type MainDataType = {
  outcomesProgressReview: RowDataType;
  outcomesProgressInformation: OutComesInfoType[];
};

export type OutComesInfoType = {
  date: string | null | Dayjs;
  progressDesc: string;
  progressRating: string;
};

export type RowDataType = {
  outcomeId: string;
  startDate: string | null | Dayjs;
  endDate: string | null | Dayjs;
  concern: string;
  outcome: string;
  measurableProcedure: string;
  targetDate: string;
  isRecordSubmitted: boolean;
  tbrUpdateRequired: boolean;
  tbrOutcomeId: number;
};

export type OutcomesProgressReviewDataType = {
  date: string | null | Dayjs;
  progressDesc: string;
  progressRating: string;
};

const outComesProgressInfoheadCells: HeadCell<OutcomesProgressReviewDataType>[] =
  [
    {
      id: 'date',
      labelId: 'Date',
      defaultLabelId: 'Date',
      numeric: false,
      requiredSorting: false,
    },
    {
      id: 'progressDesc',
      labelId: 'Progress Description',
      defaultLabelId: 'Progress Description',
      numeric: false,
      requiredSorting: false,
    },
    {
      id: 'progressRating',
      labelId: 'Progress Rating',
      defaultLabelId: 'Progress Rating',
      numeric: false,
      requiredSorting: false,
    },
  ];

export const progressRatingsList = [
  {
    id: 'progress',
    label: "We're Making Progress",
  },
  {
    id: 'adjust',
    label: "Let's Make Adjustments",
  },
  {
    id: 'noLonger',
    label: 'No Longer a priority at this time',
  },
  {
    id: 'done',
    label: 'Outcome Met - We Did It',
  },
];

const validationSchema = yup.object().shape({
  startDate: yup.string().required('IFSP.startDateRequired'),
  endDate: yup.date().nullable().typeError('IFSP.endDateRequired'),
  concern: yup.string().notRequired().nullable(),
  outcome: yup.string().required('IFSP.OutcomeRequired'),
  measurableProcedure: yup.string().notRequired().nullable(),
  targetDate: yup.string().notRequired().nullable(),
});

type DateOrNullableString = string | null | Dayjs;

const ChildandFamilyIFSPOutcomes: FC<ChildandFamilyIFSPOutcomesProps> = ({
  open,
  onOpen,
  onClose,
  canEditAfterLock,
  canDeleteAfterLock,
  isIfspScreenLocked,
  info,
  openInfo,
  closeInfo,
  openReviewInfo,
  closeReviewInfo,
  isErrorOccured,
  isErrorOccuredInOutComeEndDate,
  isErrorOccuredInOutComeStartDate,
  reviewInfo,
  hasEditPermission,
}) => {
  const {
    ifsp,
    updateIfspContext,
    initialIfsp,
    updateOutComesFieldFlag,
    updateOutComesEndDateFlag,
    updateOutComesStartDateFlag,
    isAllOutComesUpdated,
    isOutComeEndDateHasIssue,
    isOutComeStartDateHasIssue,
  } = useContext(IfspContext);
  const [startDate, setStartDate] = useState<DateOrNullableString>(null);
  const [endDate, setEndDate] = useState<DateOrNullableString>(null);
  const [progressDetails, setProgressDetails] = useState<OutComesInfoType[]>(
    Array.from(ifsp.ifspTeam.forms.childIfspOutcomes).map(() => ({
      date: null,
      progressDesc: '',
      progressRating: '',
    })),
  );

  const [isValidProgressDesc, setIsValidProgressDesc] = useState<boolean>(true);
  const [isValidProgressRating, setIsValidProgressRating] =
    useState<boolean>(true);
  const [isValidProgressInfoDate, setIsValidProgressInfoDate] =
    useState<boolean>(true);
  const [concern, setConcern] = useState<string>('');
  const [outcome, setOutcome] = useState<string>('');
  const [measurableProcedure, setMeasurableProcedure] = useState<string>('');
  const [targetDate, setTargetDate] = useState<string>('');
  const [isRecordSubmitted, setIsRecordSubmitted] = useState<boolean>(false);
  const [tbrUpdateRequired, setTbrUpdateRequired] = useState<boolean>(false);
  const [tbrOutcomeId, setTbrOutcomeId] = useState<number>(0);
  const [mainData, setMainData] = useState<MainDataType[]>(
    ifsp.ifspTeam.forms.childIfspOutcomes,
  );
  const [initialMainData, setInitialMainData] = useState<MainDataType[]>(
    ifsp.ifspTeam.forms.childIfspOutcomes,
  );
  const [showSubSection, setShowSubSection] = useState<boolean>(false);
  const [openIndex, setOpenIndex] = useState<number>(0);
  const [openedSubSectionIndex, setOpenedSubSectionIndex] = useState<number>(0);

  useEffect(() => {
    if (!hasEditPermission) {
      const handleKeyDown = (e: KeyboardEvent) => {
        if (e.key === 'Tab') {
          e.preventDefault();
        }
      };
      document.addEventListener('keydown', handleKeyDown);
      return () => document.removeEventListener('keydown', handleKeyDown);
    }
  }, [hasEditPermission]);

  const validateForms = () => {
    const errors: Partial<{
      startDate: DateOrNullableString;
      endDate: DateOrNullableString;
    }> = {};

    if (
      mainData[openIndex].outcomesProgressReview.startDate &&
      mainData[openIndex].outcomesProgressReview.endDate
    ) {
      const startD = dayjs(
        mainData[openIndex].outcomesProgressReview.startDate,
        'MM/DD/YYYY',
      );
      const endD = dayjs(
        mainData[openIndex].outcomesProgressReview.endDate,
        'MM/DD/YYYY',
      );

      if (startD.isAfter(endD)) {
        errors.endDate = 'IFSP.endDateValidationError';
      }
    }

    if (mainData[openIndex].outcomesProgressReview.startDate) {
      if (
        dayjs(mainData[openIndex].outcomesProgressReview.startDate).isBefore(
          ifsp.ifspMeetingDate,
        )
      ) {
        errors.startDate = 'IFSP.OutComeStartDateBeforeMeetingDate';
      }
    }

    if (
      datePickerMinDateMaxDateValidate(
        mainData[openIndex].outcomesProgressReview.startDate,
      )
    ) {
      errors.startDate = 'datePickerMinDateMaxDateValidate';
    }
    if (
      datePickerMinDateMaxDateValidate(
        mainData[openIndex].outcomesProgressReview.endDate,
      )
    ) {
      errors.endDate = 'datePickerMinDateMaxDateValidate';
    }

    return errors;
  };

  const formikRef = useRef<
    FormikProps<{
      outcomeId: string;
      startDate: DateOrNullableString;
      endDate: DateOrNullableString;
      concern: string;
      outcome: string;
      measurableProcedure: string;
      targetDate: string;
      isRecordSubmitted: boolean;
      tbrOutcomeId: number;
      tbrUpdateRequired: boolean;
    }>
  >(null);

  const addMoreProgressReview = (
    index: number,
    values: {
      startDate: string | null | Dayjs;
      endDate: string | null | Dayjs;
      concern: string;
      outcome: string;
      measurableProcedure: string;
      targetDate: string;
      isRecordSubmitted: boolean;
      tbrUpdateRequired: boolean;
      tbrOutcomeId: number;
    },
  ) => {
    const updatedProgressReviewData = (mainData[index].outcomesProgressReview =
      {
        ...mainData[index].outcomesProgressReview,
        startDate: values.startDate
          ? dayjs(values.startDate).format('MM/DD/YYYY')
          : null,
        endDate: values.endDate
          ? dayjs(values.endDate).format('MM/DD/YYYY')
          : null,
        concern: values.concern,
        measurableProcedure: values.measurableProcedure,
        outcome: values.outcome,
        targetDate: values.targetDate,
        tbrOutcomeId: values.tbrOutcomeId,
        tbrUpdateRequired: values.tbrUpdateRequired
          ? true
          : _.isEqual(
              initialIfsp.ifspTeam.forms.childIfspOutcomes[index]
                .outcomesProgressReview,
              mainData[index].outcomesProgressReview,
            ),
      });
    setMainData((prevData) => {
      const tempData = [...prevData];
      tempData[index].outcomesProgressReview = updatedProgressReviewData;
      return tempData;
    });
    setStartDate(null);
    setEndDate(null);
    setConcern('');
    setOutcome('');
    setMeasurableProcedure('');
    setTargetDate('');
    setIsRecordSubmitted(false);
    setTbrOutcomeId(0);
    setTbrUpdateRequired(false);
  };

  const addSubSection = () => {
    setIsValidProgressDesc(true);
    setIsValidProgressRating(true);
    setIsValidProgressInfoDate(true);
    setInitialMainData([
      ...initialMainData,
      {
        outcomesProgressReview: {
          outcomeId: '',
          startDate: null,
          endDate: null,
          concern: '',
          outcome: '',
          measurableProcedure: '',
          targetDate: '',
          isRecordSubmitted: false,
          tbrOutcomeId: 0,
          tbrUpdateRequired: true,
        },
        outcomesProgressInformation: [],
      },
    ]);
    setMainData([
      ...mainData,
      {
        outcomesProgressReview: {
          outcomeId: '',
          startDate: null,
          endDate: null,
          concern: '',
          outcome: '',
          measurableProcedure: '',
          targetDate: '',
          isRecordSubmitted: false,
          tbrOutcomeId: 0,
          tbrUpdateRequired: true,
        },
        outcomesProgressInformation: [],
      },
    ]);
    setProgressDetails([
      ...progressDetails,
      { date: null, progressDesc: '', progressRating: '' },
    ]);
    setOpenIndex(mainData.length - 1);
  };

  const deleteSubSection = (index: number) => {
    updateIfspContext({
      ...ifsp,
      isEIServiceOrOutcomesInfoDeleted:
        !!mainData[index].outcomesProgressReview.outcomeId,
    });
    const newSdpData = mainData.filter((__, i) => i !== index);
    const newProgressInfoData = progressDetails.filter((__, i) => i !== index);
    setMainData(newSdpData);
    setProgressDetails(newProgressInfoData);
    setShowSubSection(false);
  };

  useEffect(() => {
    formikRef.current?.setValues({
      outcomeId:
        ifsp.ifspTeam.forms.childIfspOutcomes[openIndex].outcomesProgressReview
          .outcomeId,
      startDate:
        ifsp.ifspTeam.forms.childIfspOutcomes[openIndex].outcomesProgressReview
          .startDate,
      endDate:
        ifsp.ifspTeam.forms.childIfspOutcomes[openIndex].outcomesProgressReview
          .endDate,
      concern:
        ifsp.ifspTeam.forms.childIfspOutcomes[openIndex].outcomesProgressReview
          .concern,
      outcome:
        ifsp.ifspTeam.forms.childIfspOutcomes[openIndex].outcomesProgressReview
          .outcome,
      measurableProcedure:
        ifsp.ifspTeam.forms.childIfspOutcomes[openIndex].outcomesProgressReview
          .measurableProcedure,
      targetDate:
        ifsp.ifspTeam.forms.childIfspOutcomes[openIndex].outcomesProgressReview
          .targetDate,
      isRecordSubmitted:
        ifsp.ifspTeam.forms.childIfspOutcomes[openIndex].outcomesProgressReview
          .isRecordSubmitted,
      tbrOutcomeId:
        ifsp.ifspTeam.forms.childIfspOutcomes[openIndex].outcomesProgressReview
          .tbrOutcomeId,
      tbrUpdateRequired:
        ifsp.ifspTeam.forms.childIfspOutcomes[openIndex].outcomesProgressReview
          .tbrUpdateRequired,
    });
    setStartDate(
      ifsp.ifspTeam.forms.childIfspOutcomes[openIndex].outcomesProgressReview
        .startDate,
    );
    setEndDate(
      ifsp.ifspTeam.forms.childIfspOutcomes[openIndex].outcomesProgressReview
        .endDate,
    );
    setConcern(
      ifsp.ifspTeam.forms.childIfspOutcomes[openIndex].outcomesProgressReview
        .concern,
    );
    setOutcome(
      ifsp.ifspTeam.forms.childIfspOutcomes[openIndex].outcomesProgressReview
        .outcome,
    );
    setMeasurableProcedure(
      ifsp.ifspTeam.forms.childIfspOutcomes[openIndex].outcomesProgressReview
        .measurableProcedure,
    );
    setTargetDate(
      ifsp.ifspTeam.forms.childIfspOutcomes[openIndex].outcomesProgressReview
        .targetDate,
    );
    setIsRecordSubmitted(
      ifsp.ifspTeam.forms.childIfspOutcomes[openIndex].outcomesProgressReview
        .isRecordSubmitted,
    );
    setTbrOutcomeId(
      ifsp.ifspTeam.forms.childIfspOutcomes[openIndex].outcomesProgressReview
        .tbrOutcomeId,
    );
    setTbrUpdateRequired(
      ifsp.ifspTeam.forms.childIfspOutcomes[openIndex].outcomesProgressReview
        .tbrUpdateRequired,
    );
  }, [openIndex, showSubSection]);

  useEffect(() => {
    setShowSubSection(true);
    setMainData(ifsp.ifspTeam.forms.childIfspOutcomes);
    setInitialMainData(ifsp.ifspTeam.forms.childIfspOutcomes);
  }, [open]);

  useEffect(() => {
    if (!mainData.length) return;

    updateIfspContext({
      ...ifsp,
      ifspTeam: {
        ...ifsp.ifspTeam,
        forms: {
          ...ifsp.ifspTeam.forms,
          childIfspOutcomes: mainData.map((data) => {
            const currentReview = data.outcomesProgressReview;
            const initialReview = initialMainData.find(
              (initialData) =>
                initialData.outcomesProgressReview.outcomeId ===
                currentReview.outcomeId,
            )?.outcomesProgressReview;

            const isUnchanged =
              initialReview && _.isEqual(initialReview, currentReview);

            return {
              ...data,
              outcomesProgressReview: {
                ...currentReview,
                tbrUpdateRequired:
                  !isUnchanged || currentReview.tbrUpdateRequired,
              },
            };
          }),
        },
      },
    });

    // Validate data & set error flags
    const hasError = mainData.some((data) => {
      return (
        !data.outcomesProgressReview.startDate ||
        datePickerMinDateMaxDateValidate(data.outcomesProgressReview.endDate) ||
        dayjs(data.outcomesProgressReview.startDate, 'MM/DD/YYYY').isAfter(
          dayjs(data.outcomesProgressReview.endDate, 'MM/DD/YYYY'),
        ) ||
        !data.outcomesProgressReview.outcome
      );
    });

    const hasErrorInEndDate = mainData.some((data) => {
      return dayjs(data.outcomesProgressReview.startDate, 'MM/DD/YYYY').isAfter(
        dayjs(data.outcomesProgressReview.endDate, 'MM/DD/YYYY'),
      );
    });

    const hasErrorInStartDate = mainData.some((data) => {
      return dayjs(
        data.outcomesProgressReview.startDate,
        'MM/DD/YYYY',
      ).isBefore(dayjs(ifsp.ifspMeetingDate, 'MM/DD/YYYY'));
    });
    isErrorOccuredInOutComeStartDate(hasErrorInStartDate);
    isErrorOccuredInOutComeEndDate(hasErrorInEndDate);
    isErrorOccured(hasError);
    if (!hasError) {
      updateOutComesFieldFlag(false);
    }
    if (!hasErrorInEndDate) {
      updateOutComesEndDateFlag(false);
    }

    if (!hasErrorInStartDate) {
      updateOutComesStartDateFlag(false);
      formikRef.current?.setFieldTouched('startDate', false);
      formikRef.current?.setFieldTouched('outcome', false);
    }
  }, [
    mainData,
    openIndex,
    open,
    ifsp.ifspMeetingDate,
    isAllOutComesUpdated,
    isOutComeEndDateHasIssue,
    isOutComeStartDateHasIssue,
    openedSubSectionIndex,
    showSubSection,
  ]);

  useEffect(() => {
    setOpenIndex(mainData.length - 1);
  }, [mainData.length]);

  useEffect(() => {
    if (isAllOutComesUpdated) {
      formikRef.current?.setFieldTouched('startDate', true);
      formikRef.current?.setFieldTouched('endDate', true);
      formikRef.current?.setFieldTouched('outcome', true);
    }
  }, [isAllOutComesUpdated, mainData, openIndex, open, showSubSection]);

  const renderDateRange = (data: MainDataType) => {
    return (
      <Box className="flex__ align__items__center">
        <MediumTypography
          label={dayjs(data.outcomesProgressReview.startDate).format(
            'MM/DD/YYYY',
          )}
          textColor="var(--thom-green-primary)"
          fontSize="14px"
          fontweight={600}
          className="ml-md"
        />
        <MediumTypography
          labelid="r"
          defaultlabel="to"
          textColor="var(--thom-green-primary)"
          fontSize="14px"
          fontweight={600}
          className="mr-sm ml-sm"
        />
        <MediumTypography
          label={
            data.outcomesProgressReview.endDate
              ? dayjs(data.outcomesProgressReview.endDate).format('MM/DD/YYYY')
              : '-'
          }
          textColor="var(--thom-green-primary)"
          fontSize="14px"
          fontweight={600}
        />
      </Box>
    );
  };

  return (
    <Formik
      initialValues={{
        outcomeId: '',
        startDate,
        endDate,
        concern,
        measurableProcedure,
        outcome,
        targetDate,
        isRecordSubmitted,
        tbrUpdateRequired,
        tbrOutcomeId,
      }}
      validationSchema={validationSchema}
      validate={validateForms}
      innerRef={formikRef}
      onSubmit={(values) => {
        addMoreProgressReview(openedSubSectionIndex, values);
      }}
    >
      {({
        values,
        setFieldValue,
        errors,
        touched,
        handleSubmit,
        setFieldTouched,
      }) => {
        return (
          <Box component="main">
            {info && (
              <InformationModal
                open={info}
                titleLabel="IFSP.Information"
                titleDefaultLabel="Information"
                buttonLabel=" IFSP.InfoOkButton"
                buttonDefaultLabel="Ok"
                onClose={() => {
                  closeInfo(info);
                }}
                info={ChildandFamilyIFSPOutcomesData.info}
              />
            )}
            {reviewInfo && (
              <InformationModal
                open={reviewInfo}
                titleLabel="IFSP.Information"
                titleDefaultLabel="Information"
                buttonLabel=" IFSP.InfoOkButton"
                buttonDefaultLabel="Ok"
                onClose={() => {
                  closeReviewInfo(reviewInfo);
                }}
                info={ChildandFamilyIFSPOutcomesData.reviewInfo}
              />
            )}
            <Box
              component="section"
              className="p-lg mt-lg borderRadius4"
              sx={{
                border: '1px solid #00C6B8',
                backgroundColor: '#ECF9F8',
              }}
            >
              <Box component="div" className="flex__justify__space-between">
                {open ? (
                  <Box component="div" className="flex__justify__space-between">
                    <MediumTypography
                      labelid="IFSP.session3"
                      defaultlabel="Child And Family IFSP Outcomes"
                      fontSize="18px"
                      fontweight={600}
                      textColor="#2A4241"
                      className="pr-md"
                    />
                    <InfoIcon
                      className="cursorPointer"
                      onClick={() => {
                        openInfo(info);
                      }}
                    />
                  </Box>
                ) : (
                  <MediumTypography
                    labelid="IFSP.session3"
                    defaultlabel="Child And Family IFSP Outcomes"
                    fontSize="18px"
                    fontweight={600}
                    textColor="#2A4241"
                  />
                )}
                {!open ? (
                  <DownArrow
                    className="cursorPointer"
                    style={{ color: '#008C82' }}
                    onClick={() => {
                      onOpen(open);
                      if (isAllOutComesUpdated) {
                        setFieldTouched('startDate', true);
                        setFieldTouched('endDate', true);
                        setFieldTouched('outcome', true);
                      } else if (isOutComeStartDateHasIssue) {
                        setFieldTouched('startDate', true);
                        setFieldTouched('outcome', true);
                      } else if (isOutComeEndDateHasIssue) {
                        setFieldTouched('endDate', true);
                      }
                    }}
                  />
                ) : (
                  <UpArrow
                    className="cursorPointer"
                    onClick={() => {
                      onClose(open);
                    }}
                  />
                )}
              </Box>
              {open && (
                <Box component="div">
                  {mainData.map((maindataForRef, subSectionIndex) => (
                    <Box
                      component="div"
                      key={`${maindataForRef.outcomesProgressInformation[subSectionIndex]?.progressRating}-${subSectionIndex}`}
                    >
                      <Box
                        component="div"
                        className="mt-lg mb-lg"
                        sx={{
                          borderTop:
                            subSectionIndex === 0
                              ? '1px solid #00C6B8'
                              : 'none',
                          paddingTop: subSectionIndex === 0 ? '24px' : '0px',
                          paddingBottom:
                            showSubSection && openIndex === subSectionIndex
                              ? '0px'
                              : '24px',
                          borderBottom:
                            showSubSection && openIndex === subSectionIndex
                              ? 'none'
                              : '1px solid #00C6B8',
                        }}
                      >
                        <Box className="flex__justify__space-between">
                          <Box
                            component="div"
                            className="flex__ align__items__center"
                          >
                            {showSubSection && openIndex === subSectionIndex ? (
                              <HideIcon
                                onClick={() => {
                                  setShowSubSection(false);
                                  setOpenIndex(subSectionIndex);
                                  setIsValidProgressDesc(true);
                                  setIsValidProgressRating(true);
                                  setIsValidProgressInfoDate(true);
                                }}
                                className="cursorPointer"
                              />
                            ) : (
                              <FilledAddIcon
                                onClick={() => {
                                  setShowSubSection(true);
                                  if (isAllOutComesUpdated) {
                                    setFieldTouched('startDate', true);
                                    setFieldTouched('endDate', true);
                                    setFieldTouched('outcome', true);
                                  } else if (isOutComeStartDateHasIssue) {
                                    setFieldTouched('startDate', true);
                                    setFieldTouched('outcome', true);
                                  } else if (isOutComeEndDateHasIssue) {
                                    setFieldTouched('endDate', true);
                                  }
                                  setOpenIndex(subSectionIndex);
                                  setIsValidProgressDesc(true);
                                  setIsValidProgressRating(true);
                                  setIsValidProgressInfoDate(true);
                                }}
                                className="cursorPointer"
                              />
                            )}
                            <MediumTypography
                              labelid="IFSP.outComes"
                              defaultlabel="Outcomes"
                              textColor="#2A4241"
                              fontSize="16px"
                              fontweight={600}
                              className="ml-sm mr-xs"
                            />
                            <InfoIcon
                              className="cursorPointer"
                              onClick={() => {
                                openReviewInfo(reviewInfo);
                              }}
                            />
                            {(!showSubSection ||
                              openIndex !== subSectionIndex) &&
                              maindataForRef.outcomesProgressReview.startDate &&
                              renderDateRange(maindataForRef)}
                          </Box>
                          <Box component="div" className=" flex__ ">
                            <Box
                              component="div"
                              className="cursorPointer flex__ align__items__center visibility__hidden"
                            >
                              <PrinterIcon />
                              <MediumTypography
                                labelid="r"
                                defaultlabel="Print"
                                textColor="#2A4241"
                                fontweight={700}
                                fontSize="16px"
                                className="ml-xs"
                              />
                            </Box>
                            {((mainData.length > 1 &&
                              !isIfspScreenLocked &&
                              mainData[subSectionIndex].outcomesProgressReview
                                .isRecordSubmitted === false &&
                              mainData[subSectionIndex].outcomesProgressReview
                                .tbrOutcomeId < 1) ||
                              (canDeleteAfterLock && mainData.length > 1)) && (
                              <Box
                                component="div"
                                className="cursorPointer flex__ align__items__center ml-lg"
                                onClick={() => {
                                  if (
                                    !isIfspScreenLocked ||
                                    canDeleteAfterLock
                                  ) {
                                    deleteSubSection(subSectionIndex);
                                  }
                                }}
                              >
                                <Delete />
                                <MediumTypography
                                  labelid="IFSP.Delete"
                                  defaultlabel="Delete"
                                  textColor="#EB4C60"
                                  fontweight={700}
                                  fontSize="16px"
                                  className="ml-xs"
                                />
                              </Box>
                            )}
                          </Box>
                        </Box>
                        {showSubSection &&
                        openIndex === subSectionIndex ? null : (
                          <Tooltip
                            placement="bottom"
                            title={
                              maindataForRef.outcomesProgressReview.outcome ??
                              '-'
                            }
                            arrow={true}
                            followCursor
                          >
                            <Box
                              sx={{
                                width: 'fit-content',
                                maxWidth: '70%',
                              }}
                            >
                              <MediumTypography
                                label={
                                  maindataForRef.outcomesProgressReview
                                    .outcome || '-'
                                }
                                textColor="#2A4241"
                                fontSize="14px"
                                textstyle="justify"
                                fontweight={500}
                                sxProps={{
                                  width: 'fit-content',
                                  maxWidth: '100%',
                                  marginLeft: '33px',
                                  display: '-webkit-box',
                                  WebkitLineClamp: 2,
                                  WebkitBoxOrient: 'vertical',
                                  overflow: 'hidden',
                                  textOverflow: 'ellipsis',
                                  wordBreak: 'break-word',
                                }}
                                className="mt-sm"
                              />
                            </Box>
                          </Tooltip>
                        )}
                      </Box>

                      {showSubSection && openIndex === subSectionIndex && (
                        <Box
                          component="div"
                          className="pb-lg"
                          sx={{
                            borderBottom: '1px solid #00C6B8',
                            pointerEvents: hasEditPermission ? 'auto' : 'none',
                            opacity: hasEditPermission ? 'unset' : '0.8',
                          }}
                        >
                          <Box component="div">
                            <Grid container rowSpacing={'16px'}>
                              <Grid item xs={5}>
                                <MediumTypography
                                  labelid={'IFSP.consernPriority'}
                                  defaultlabel={'Concern Priority:'}
                                  className="mb-xs"
                                />
                                <CommentTextbox
                                  disabled={
                                    isIfspScreenLocked && !canEditAfterLock
                                  }
                                  placeholder="Enter comments here"
                                  name="concernPriority"
                                  Value={values.concern}
                                  maxLength={500}
                                  handlechange={(text) => {
                                    setFieldValue('concern', text);
                                    setMainData((prev) =>
                                      prev.map((item, index) =>
                                        index === subSectionIndex
                                          ? {
                                              ...item,
                                              outcomesProgressReview: {
                                                ...item.outcomesProgressReview,
                                                concern: text,
                                              },
                                            }
                                          : item,
                                      ),
                                    );
                                  }}
                                  rows={3}
                                  placeholderBackgroundColor="#fff"
                                />
                                {errors.concern && touched.concern && (
                                  <MediumTypography
                                    className="errorText-md"
                                    labelid={errors.concern}
                                    defaultlabel="Concern Priority is Required"
                                  />
                                )}
                              </Grid>
                              <Grid item xs={0.5}></Grid>

                              <Grid item xs={5}>
                                <MediumTypography
                                  labelid={'IFSP.outcomeChildFamily'}
                                  defaultlabel={
                                    'What changes do we want to see happen for our child and family?'
                                  }
                                  className="mb-xs"
                                />
                                <CommentTextbox
                                  disabled={
                                    (isIfspScreenLocked ||
                                      values.isRecordSubmitted ||
                                      values.tbrOutcomeId > 0) &&
                                    !canEditAfterLock
                                  }
                                  placeholder="Enter comments here"
                                  name="What changes do we want to see happen for our child and family?"
                                  Value={values.outcome}
                                  maxLength={500}
                                  handlechange={(text) => {
                                    setFieldValue('outcome', text);
                                    setMainData((prev) =>
                                      prev.map((item, index) =>
                                        index === subSectionIndex
                                          ? {
                                              ...item,
                                              outcomesProgressReview: {
                                                ...item.outcomesProgressReview,
                                                outcome: text,
                                              },
                                            }
                                          : item,
                                      ),
                                    );
                                  }}
                                  rows={3}
                                  Required
                                  placeholderBackgroundColor="#fff"
                                />
                                {errors.outcome && touched.outcome && (
                                  <MediumTypography
                                    className="errorText-md"
                                    labelid={errors.outcome}
                                    defaultlabel="Outcome is Required"
                                  />
                                )}
                              </Grid>
                              <Grid item xs={5}>
                                <MediumTypography
                                  labelid={'IFSP.outComeMeasurable'}
                                  defaultlabel={
                                    'How will we measure and know when we have met this outcome?'
                                  }
                                  className="mb-xs"
                                />
                                <CommentTextbox
                                  disabled={
                                    isIfspScreenLocked && !canEditAfterLock
                                  }
                                  placeholder="Enter comments here"
                                  name="measurableProcedure"
                                  Value={values.measurableProcedure}
                                  maxLength={500}
                                  handlechange={(text) => {
                                    setFieldValue('measurableProcedure', text);
                                    setMainData((prev) =>
                                      prev.map((item, index) =>
                                        index === subSectionIndex
                                          ? {
                                              ...item,
                                              outcomesProgressReview: {
                                                ...item.outcomesProgressReview,
                                                measurableProcedure: text,
                                              },
                                            }
                                          : item,
                                      ),
                                    );
                                  }}
                                  rows={3}
                                  placeholderBackgroundColor="#fff"
                                />
                                {errors.measurableProcedure &&
                                  touched.measurableProcedure && (
                                    <MediumTypography
                                      className="errorText-md"
                                      labelid={errors.measurableProcedure}
                                      defaultlabel="Measurable Criteria & Procedures is Required"
                                    />
                                  )}
                              </Grid>
                              <Grid item xs={0.5}></Grid>

                              <Grid item xs={5}>
                                <MediumTypography
                                  labelid={'IFSP.outComeDesired'}
                                  defaultlabel={
                                    'When do we think we might achieve the desired outcome?'
                                  }
                                  className="mb-xs"
                                />
                                <CommentTextbox
                                  disabled={
                                    isIfspScreenLocked && !canEditAfterLock
                                  }
                                  placeholder="Enter comments here"
                                  name="targetDate"
                                  Value={values.targetDate}
                                  maxLength={500}
                                  handlechange={(text) => {
                                    setFieldValue('targetDate', text);
                                    setMainData((prev) =>
                                      prev.map((item, index) =>
                                        index === subSectionIndex
                                          ? {
                                              ...item,
                                              outcomesProgressReview: {
                                                ...item.outcomesProgressReview,
                                                targetDate: text,
                                              },
                                            }
                                          : item,
                                      ),
                                    );
                                  }}
                                  rows={3}
                                  placeholderBackgroundColor="#fff"
                                />
                                {errors.targetDate && touched.targetDate && (
                                  <MediumTypography
                                    className="errorText-md"
                                    labelid={errors.targetDate}
                                    defaultlabel="Time Frame is Required"
                                  />
                                )}
                              </Grid>
                              <Grid item xs={5.5}>
                                <DatePickerComponent
                                  disabledDate={
                                    (isIfspScreenLocked ||
                                      values.isRecordSubmitted ||
                                      values.tbrOutcomeId > 0) &&
                                    !canEditAfterLock
                                  }
                                  className="bg__white"
                                  required={true}
                                  labelid={'IFSP.startDate'}
                                  defaultlabelid={'Start Date*'}
                                  disableFuture={false}
                                  value={
                                    values.startDate
                                      ? dayjs(values.startDate)
                                      : null
                                  }
                                  handlechange={(dateValue: Dayjs | null) => {
                                    if (dateValue) {
                                      const formattedDate =
                                        dayjs(dateValue).format('MM/DD/YYYY');
                                      setFieldValue('startDate', formattedDate);
                                      setMainData((prev) =>
                                        prev.map((item, index) =>
                                          index === subSectionIndex
                                            ? {
                                                ...item,
                                                outcomesProgressReview: {
                                                  ...item.outcomesProgressReview,
                                                  startDate: formattedDate,
                                                },
                                              }
                                            : item,
                                        ),
                                      );
                                    } else {
                                      setFieldValue('startDate', null);
                                      setStartDate(null);
                                      setMainData((prev) =>
                                        prev.map((item, index) =>
                                          index === subSectionIndex
                                            ? {
                                                ...item,
                                                outcomesProgressReview: {
                                                  ...item.outcomesProgressReview,
                                                  startDate: null,
                                                },
                                              }
                                            : item,
                                        ),
                                      );
                                    }
                                  }}
                                />
                                {errors.startDate && touched.startDate && (
                                  <MediumTypography
                                    className="errorText-md"
                                    labelid={errors.startDate}
                                    defaultlabel="Start Date is Required"
                                  />
                                )}
                              </Grid>
                              <Grid item xs={0.5}></Grid>
                              <Grid item xs={5.5}>
                                <DatePickerComponent
                                  disabledDate={
                                    isIfspScreenLocked && !canEditAfterLock
                                  }
                                  className="bg__white"
                                  labelid={'IFSP.endDate'}
                                  defaultlabelid={'End Date'}
                                  disableFuture={false}
                                  value={
                                    values.endDate
                                      ? dayjs(values.endDate)
                                      : null
                                  }
                                  handlechange={(dateValue: Dayjs | null) => {
                                    if (dateValue) {
                                      const formattedDate =
                                        dayjs(dateValue).format('MM/DD/YYYY');
                                      setFieldValue('endDate', formattedDate);
                                      setMainData((prev) =>
                                        prev.map((item, index) =>
                                          index === subSectionIndex
                                            ? {
                                                ...item,
                                                outcomesProgressReview: {
                                                  ...item.outcomesProgressReview,
                                                  endDate: formattedDate,
                                                },
                                              }
                                            : item,
                                        ),
                                      );
                                    } else {
                                      setFieldValue('endDate', null);
                                      setEndDate(null);
                                      setMainData((prev) =>
                                        prev.map((item, index) =>
                                          index === subSectionIndex
                                            ? {
                                                ...item,
                                                outcomesProgressReview: {
                                                  ...item.outcomesProgressReview,
                                                  endDate: null,
                                                },
                                              }
                                            : item,
                                        ),
                                      );
                                    }
                                  }}
                                />
                                {errors.endDate && touched.endDate && (
                                  <MediumTypography
                                    className="errorText-md"
                                    labelid={errors.endDate}
                                    defaultlabel="End Date is Required"
                                  />
                                )}
                              </Grid>
                              <Grid
                                item
                                xs={1}
                                display="none"
                                className="cursorPointer mt-xs"
                              >
                                <Box
                                  className="cursorPointer flex__ align__items__center"
                                  onClick={() => {
                                    if (
                                      !isIfspScreenLocked ||
                                      canEditAfterLock
                                    ) {
                                      setOpenedSubSectionIndex(subSectionIndex);
                                      handleSubmit();
                                    }
                                  }}
                                >
                                  <Box className="flex__ align__items__center">
                                    <AddIcon className="cursorPointer mr-xs ml-lg" />
                                  </Box>
                                  <MediumTypography
                                    labelid={'ConsentSave'}
                                    defaultlabel={'Save'}
                                    fontweight={400}
                                    textColor="#2A4241"
                                  />
                                </Box>
                              </Grid>
                            </Grid>
                          </Box>
                          <MediumTypography
                            labelid="IFSP.outcomesProgressInformation"
                            defaultlabel="Outcomes Progress Information"
                            textColor="#2A4241"
                            fontSize="16px"
                            fontweight={600}
                            className="mt-xlg pt-lg"
                            sxProps={{ borderTop: '1px solid #00C6B8' }}
                          />
                          <Grid container rowSpacing={'16px'} className="mt-xs">
                            <Grid item xs={5}>
                              <DatePickerComponent
                                disabledDate={
                                  isIfspScreenLocked && !canEditAfterLock
                                }
                                labelid="IFSP.Date"
                                defaultlabelid="Date*"
                                required={true}
                                value={
                                  progressDetails[subSectionIndex]?.date
                                    ? dayjs(
                                        progressDetails[subSectionIndex]?.date,
                                      )
                                    : null
                                }
                                handlechange={(dateValue: Dayjs | null) => {
                                  const prevData = [...progressDetails];
                                  if (dateValue) {
                                    const formattedDate =
                                      dayjs(dateValue).format('MM/DD/YYYY');
                                    prevData[subSectionIndex].date =
                                      formattedDate;
                                    setProgressDetails(prevData);
                                    setIsValidProgressInfoDate(true);
                                  } else {
                                    prevData[subSectionIndex].date = null;
                                    setProgressDetails(prevData);
                                  }
                                }}
                                className="bg__white"
                              />
                              {!isValidProgressInfoDate && (
                                <MediumTypography
                                  className="errorText-md"
                                  labelid={
                                    datePickerMinDateMaxDateValidate(
                                      progressDetails[subSectionIndex]?.date,
                                    )
                                      ? 'datePickerMinDateMaxDateValidate'
                                      : 'IFSP.DateRequired'
                                  }
                                  defaultlabel="Date is Required"
                                />
                              )}
                            </Grid>
                            <Grid item xs={0.5}></Grid>
                            <Grid item xs={5}></Grid>
                            <Grid item xs={5}>
                              <CommentTextbox
                                disabled={
                                  isIfspScreenLocked && !canEditAfterLock
                                }
                                placeholder="Progress Description"
                                name="IFSP.progressDesc"
                                Value={
                                  progressDetails[subSectionIndex]?.progressDesc
                                }
                                maxLength={500}
                                handlechange={(text) => {
                                  const prevData = [...progressDetails];
                                  prevData[subSectionIndex].progressDesc = text;
                                  setProgressDetails(prevData);
                                  setIsValidProgressDesc(true);
                                }}
                                rows={4}
                                Required
                                placeholderBackgroundColor="#fff"
                              />
                              {!isValidProgressDesc && (
                                <MediumTypography
                                  className="errorText-md"
                                  labelid={'IFSP.progressDescRequired'}
                                  defaultlabel="Progress Description is Required"
                                />
                              )}
                            </Grid>
                            <Grid item xs={5}></Grid>
                            <Grid item xs={5}>
                              <DropdownComponent
                                disabled={
                                  (isIfspScreenLocked && !canEditAfterLock) ||
                                  !hasEditPermission
                                }
                                names={progressRatingsList}
                                defaultlabelid="Progress Rating"
                                labelid="IFSP.progressRating"
                                value={
                                  progressDetails[subSectionIndex]
                                    ?.progressRating
                                }
                                handleChange={(value: string) => {
                                  const prevData = [...progressDetails];
                                  prevData[subSectionIndex].progressRating =
                                    value;
                                  setProgressDetails(prevData);
                                  setIsValidProgressRating(true);
                                }}
                                Required
                              />
                              {!isValidProgressRating && (
                                <MediumTypography
                                  className="errorText-md"
                                  labelid={'IFSP.progressRatingRequired'}
                                  defaultlabel="Progress Rating is Required"
                                />
                              )}
                            </Grid>
                            <Grid item xs={0.5}></Grid>
                            {(!isIfspScreenLocked || canEditAfterLock) && (
                              <Grid item xs={5}>
                                <Box
                                  className="cursorPointer flex__ align__items__center pt-xs"
                                  onClick={() => {
                                    if (
                                      (!isIfspScreenLocked ||
                                        canEditAfterLock) &&
                                      progressDetails[subSectionIndex]
                                        ?.progressDesc &&
                                      progressDetails[subSectionIndex]
                                        ?.progressRating &&
                                      progressDetails[subSectionIndex]?.date &&
                                      !datePickerMinDateMaxDateValidate(
                                        progressDetails[subSectionIndex]?.date,
                                      )
                                    ) {
                                      setMainData((prev) => {
                                        const tempData = [...prev];
                                        tempData[
                                          subSectionIndex
                                        ].outcomesProgressInformation.push({
                                          progressDesc:
                                            progressDetails[subSectionIndex]
                                              ?.progressDesc,
                                          progressRating:
                                            progressDetails[subSectionIndex]
                                              ?.progressRating,
                                          date: progressDetails[subSectionIndex]
                                            ?.date,
                                        });
                                        return tempData; // Return the updated array
                                      });
                                      const prevData = [...progressDetails];
                                      prevData[subSectionIndex].progressDesc =
                                        '';
                                      prevData[subSectionIndex].progressRating =
                                        '';
                                      prevData[subSectionIndex].date = null;
                                      setProgressDetails(prevData);
                                    } else {
                                      const missingFields = [
                                        !progressDetails[subSectionIndex]
                                          ?.progressDesc,
                                        !progressDetails[subSectionIndex]
                                          ?.progressRating,
                                        !progressDetails[subSectionIndex]?.date,
                                      ];

                                      const missingFieldHandlers = [
                                        () => setIsValidProgressDesc(false),
                                        () => setIsValidProgressRating(false),
                                        () => setIsValidProgressInfoDate(false),
                                      ];
                                      missingFields.forEach(
                                        (missing, index) => {
                                          if (missing)
                                            missingFieldHandlers[index]();
                                        },
                                      );
                                    }
                                  }}
                                >
                                  <Box className="flex__ align__items__center pr-sm">
                                    <AddIcon className="cursorPointer" />
                                  </Box>
                                  <MediumTypography
                                    labelid={'IFSP.SaveProgressInformation'}
                                    defaultlabel={'Save Progress Information'}
                                    fontweight={400}
                                    textColor="#2A4241"
                                  />
                                </Box>
                              </Grid>
                            )}
                          </Grid>
                          <Box
                            component="div"
                            className="respoScrollTable mt-md"
                          >
                            <TableContainer component={Paper}>
                              <Table>
                                <TableHeader
                                  headerNames={outComesProgressInfoheadCells}
                                  checkBoxRequired={false}
                                  className="listDataTableHead"
                                />
                                <TableBody className="tableRowcss ">
                                  {openIndex === subSectionIndex &&
                                    mainData[
                                      subSectionIndex
                                    ].outcomesProgressInformation.map(
                                      (
                                        listData: OutcomesProgressReviewDataType,
                                        index,
                                      ) => (
                                        <OutcomesProgressInfoTabelRow
                                          key={
                                            'outcomesProgressInformation' +
                                            index
                                          }
                                          data={listData}
                                        />
                                      ),
                                    )}
                                </TableBody>
                              </Table>
                              {mainData[subSectionIndex]
                                .outcomesProgressInformation.length === 0 && (
                                <Box component="div" className="width__100">
                                  <MediumTypography
                                    textstyle={'center'}
                                    labelid="IFSP.emptyMessage"
                                    defaultlabel="No data added yet"
                                    className="p-sm"
                                  />
                                </Box>
                              )}
                            </TableContainer>
                          </Box>
                        </Box>
                      )}
                    </Box>
                  ))}
                  {hasEditPermission && (
                    <Box component="div" className="mt-md">
                      <ButtonComponent
                        disabled={isIfspScreenLocked}
                        className="btn-primary btn-submit"
                        variantType="contained"
                        type="submit"
                        labelId="Add Additional Outcome"
                        defaultLabelId="Add Additional Outcome"
                        onClick={addSubSection}
                      />
                    </Box>
                  )}
                </Box>
              )}
            </Box>
          </Box>
        );
      }}
    </Formik>
  );
};

export default ChildandFamilyIFSPOutcomes;
